import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUzPhone, initialPhoneInput, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Truck } from "lucide-react";
import {
  api,
  fmtMoney,
  type ApiError,
  type PaymentMethod,
  type PayableSummary,
  type PayableSupplier,
  type Supplier,
  type SupplierHistory,
} from "../api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { usePageHeader } from "../lib/use-page-header";
import { ConfirmDialog } from "../components/confirm-dialog";
import { MiniKpi } from "../components/mini-kpi";
import { MoneyInput } from "../components/money-input";
import {
  PaymentMethodSelect,
  usePaymentMethodLabel,
} from "../components/payment-method-select";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Money, Mono } from "../components/ui/ledger";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

/**
 * Kartochka formasining kirish shakli — to'liq `Supplier` EMAS.
 *
 * Detal oynasi `SupplierHistory` bilan ishlaydi (uning ichida `workshop_id` ham,
 * `created_at` ham yo'q), shuning uchun forma faqat o'zi tahrirlaydigan
 * maydonlarni oladi. Aks holda tahrir tugmasi soxta `workshop_id: 0` bilan
 * yasama `Supplier` qurishga majbur bo'lardi.
 */
type SupplierDraft = {
  id: number;
  name: string;
  phone: string | null;
  contact_person: string | null;
  address: string | null;
  note: string | null;
  faol: boolean;
};

/**
 * Yetkazib beruvchilar bilan hisob-kitob — "kimga qancha qarzdorman".
 *
 * Ro'yxat shu savolga DRILL-DOWNGACHA javob beradi: balans bo'yicha kamayish
 * tartibida (server tomonda), tepasida jami kreditorlik. Qatorga bosilganda
 * to'liq harakatlar tarixi ochiladi — "Manba" ustuni xariddan kelgan qarzni
 * qo'lda yozilgan to'lovdan ajratib turadi.
 *
 * DIQQAT: bu yerdagi "jami kreditorlik" Qarzlar sahifasidagi bilan bir xil
 * bo'lishi SHART EMAS va bu xato emas. U yerda summa har bir XARID HUJJATI
 * bo'yicha qoldiqdan yig'iladi, bu yerda esa har bir YETKAZIB BERUVCHINING
 * sof balansidan — qo'lda yozilgan to'lov yoki qaytarish ikkinchisini
 * o'zgartiradi, birinchisini esa yo'q. Shuning uchun yorliqlar ham har xil.
 *
 * `/payables` INNER join: hech qanday harakati bo'lmagan yetkazib beruvchi u
 * yerda ko'rinmaydi. Shuning uchun "Barcha yetkazib beruvchilar" ko'rinishi
 * alohida — u `GET /suppliers` katalogi ustida quriladi va yangi kartochka
 * yaratish/tahrirlash uchun yagona joy.
 *
 * Bu — Ombor sahifasining tabi, shuning uchun `tovars` qorovuli takrorlanmaydi.
 */
export function YetkazibBeruvchilarPage() {
  const { t } = useTranslation(["ombor", "common"]);
  const [view, setView] = useState<"payables" | "all">("payables");
  const [summary, setSummary] = useState<PayableSummary | null>(null);
  const [payables, setPayables] = useState<PayableSupplier[]>([]);
  const [all, setAll] = useState<Supplier[]>([]);
  const [hammasi, setHammasi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [editing, setEditing] = useState<SupplierDraft | "new" | null>(null);

  const reload = useCallback(() => setRefresh((x) => x + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.supplierSummary(),
      api.payableSuppliers(hammasi),
      api.suppliers(),
    ])
      .then(([s, p, cat]) => {
        if (!alive) return;
        setSummary(s);
        setPayables(p);
        setAll(cat.items);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [hammasi, refresh, t]);

  const actionsNode = useMemo(
    () => (
      <Button size="sm" onClick={() => setEditing("new")}>
        <Plus />
        {t("yetkazib.new")}
      </Button>
    ),
    [t],
  );
  usePageHeader({ actions: actionsNode });

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {err}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <MiniKpi
          label={t("yetkazib.kpi.jami")}
          value={fmtMoney(summary?.jami_kreditorlik ?? null)}
        />
        <MiniKpi
          label={t("yetkazib.kpi.soni")}
          value={String(summary?.qarzdor_yetkazib_beruvchilar_soni ?? 0)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <PillButton
          active={view === "payables"}
          onClick={() => setView("payables")}
        >
          {t("yetkazib.views.payables")}
        </PillButton>
        <PillButton active={view === "all"} onClick={() => setView("all")}>
          {t("yetkazib.views.all")}
        </PillButton>
        {view === "payables" && (
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={hammasi}
              onChange={(e) => setHammasi(e.target.checked)}
              className="size-4 accent-primary"
            />
            {t("yetkazib.showSettled")}
          </label>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : view === "payables" ? (
            payables.length === 0 ? (
              <EmptyState
                icon={<Truck />}
                title={t("yetkazib.empty.payables.title")}
                description={t("yetkazib.empty.payables.desc")}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("yetkazib.table.nomi")}</TableHead>
                    <TableHead>{t("yetkazib.table.aloqa")}</TableHead>
                    <TableHead>{t("yetkazib.table.telefon")}</TableHead>
                    <TableHead className="text-right">
                      {t("yetkazib.table.balans")}
                    </TableHead>
                    <TableHead>{t("yetkazib.table.oxirgi")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payables.map((s) => (
                    <TableRow
                      key={s.supplier_id}
                      className="cursor-pointer"
                      onClick={() => setDetailId(s.supplier_id)}
                    >
                      <TableCell className="font-medium">
                        {s.nomi}
                        {!s.faol && (
                          <Badge variant="outline" className="ml-2">
                            {t("yetkazib.inactive")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.aloqa_shaxsi || "—"}
                      </TableCell>
                      <TableCell>
                        <Mono className="text-muted-foreground">
                          {formatUzPhone(s.telefon) || "—"}
                        </Mono>
                      </TableCell>
                      <TableCell className="text-right">
                        <BalansCell balans={s.balans} t={t} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.oxirgi_harakat ? fmtDate(s.oxirgi_harakat) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : all.length === 0 ? (
            <EmptyState
              icon={<Truck />}
              title={t("yetkazib.empty.all.title")}
              description={t("yetkazib.empty.all.desc")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("yetkazib.table.nomi")}</TableHead>
                  <TableHead>{t("yetkazib.table.aloqa")}</TableHead>
                  <TableHead>{t("yetkazib.table.telefon")}</TableHead>
                  <TableHead>{t("yetkazib.table.manzil")}</TableHead>
                  <TableHead className="text-right">
                    {t("yetkazib.table.amallar")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {all.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer"
                    onClick={() => setDetailId(s.id)}
                  >
                    <TableCell className="font-medium">
                      {s.name}
                      {!s.faol && (
                        <Badge variant="outline" className="ml-2">
                          {t("yetkazib.inactive")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.contact_person || "—"}
                    </TableCell>
                    <TableCell>
                      <Mono className="text-muted-foreground">
                        {formatUzPhone(s.phone) || "—"}
                      </Mono>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {s.address || "—"}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(s)}
                      >
                        <Pencil />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detailId !== null && (
        <SupplierDetailDialog
          supplierId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={reload}
          onEdit={(s) => setEditing(s)}
        />
      )}

      {editing !== null && (
        <SupplierFormDialog
          supplier={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-primary px-3 py-1.5 text-[13px] font-semibold text-primary-foreground"
          : "rounded-full border px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground"
      }
    >
      {children}
    </button>
  );
}

/** Musbat = biz qarzdormiz (qizil); manfiy = avans/kredit (yashil). */
function BalansCell({ balans, t }: { balans: string; t: TFunction }) {
  const n = Number(balans);
  if (n === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-col items-end">
      <Money tone={n > 0 ? "neg" : "pos"}>{fmtMoney(Math.abs(n))}</Money>
      {n < 0 && (
        <span className="text-[11px] text-muted-foreground">
          {t("yetkazib.avans")}
        </span>
      )}
    </span>
  );
}

/* ───────────────────────── Detal + hisob-kitob ───────────────────────── */

function SupplierDetailDialog({
  supplierId,
  onClose,
  onChanged,
  onEdit,
}: {
  supplierId: number;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (s: SupplierDraft) => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const usulLabel = usePaymentMethodLabel();
  const [data, setData] = useState<SupplierHistory | null>(null);
  const [local, setLocal] = useState(0);
  const [pay, setPay] = useState(false);
  const [manual, setManual] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .supplierHistory(supplierId)
      .then((d) => alive && setData(d))
      .catch(() => alive && onClose());
    return () => {
      alive = false;
    };
  }, [supplierId, local, onClose]);

  const bump = () => {
    setLocal((x) => x + 1);
    onChanged();
  };

  async function deactivate() {
    try {
      await api.deactivateSupplier(supplierId);
      toast.success(t("yetkazib.deactivated"));
      bump();
    } catch (e) {
      toast.error(
        (e as { message?: string }).message || t("common:common.error"),
      );
      throw e; // ConfirmDialog ochiq qoladi
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()} contentClassName="max-w-2xl">
        <div className="flex max-h-[85vh] flex-col">
          {data === null ? (
            <div className="space-y-2 p-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex-none space-y-3 border-b p-5">
                <div>
                  <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
                    {data.nomi}
                    {!data.faol && (
                      <Badge variant="outline" className="ml-2">
                        {t("yetkazib.inactive")}
                      </Badge>
                    )}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {[data.aloqa_shaxsi, formatUzPhone(data.telefon), data.manzil]
                      .filter(Boolean)
                      .join(" · ") || t("yetkazib.noContact")}
                  </p>
                  {data.izoh && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {data.izoh}
                    </p>
                  )}
                </div>

                <div className="flex items-end justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2.5">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("yetkazib.detail.balans")}
                  </span>
                  <BalansCell balans={data.balans} t={t} />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setPay(true)}>
                    {t("yetkazib.detail.tolov")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setManual(true)}>
                    {t("yetkazib.detail.qolda")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      onEdit({
                        id: data.supplier_id,
                        name: data.nomi,
                        phone: data.telefon,
                        contact_person: data.aloqa_shaxsi,
                        address: data.manzil,
                        note: data.izoh,
                        faol: data.faol,
                      })
                    }
                  >
                    <Pencil />
                    {t("yetkazib.detail.edit")}
                  </Button>
                  {data.faol && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmOff(true)}
                    >
                      <Trash2 />
                      {t("yetkazib.detail.deactivate")}
                    </Button>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("yetkazib.detail.tarix")}
                </h3>
                {data.harakatlar.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t("yetkazib.detail.noHistory")}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("yetkazib.tx.sana")}</TableHead>
                        <TableHead>{t("yetkazib.tx.tur")}</TableHead>
                        <TableHead>{t("yetkazib.tx.manba")}</TableHead>
                        <TableHead className="text-right">
                          {t("yetkazib.tx.summa")}
                        </TableHead>
                        <TableHead>{t("common:paymentMethod.label")}</TableHead>
                        <TableHead>{t("yetkazib.tx.izoh")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.harakatlar.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="whitespace-nowrap">
                            <Mono className="text-muted-foreground">
                              {fmtDateTime(tx.created_at)}
                            </Mono>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                tx.tur === "qarz" ? "destructive" : "success"
                              }
                            >
                              {t(`yetkazib.tx.${tx.tur}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {tx.manba === "purchase" && tx.manba_id
                              ? t("yetkazib.tx.manbaXarid", { id: tx.manba_id })
                              : t(`yetkazib.tx.manba_${tx.manba}`)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Money tone={tx.tur === "qarz" ? "neg" : "pos"}>
                              {fmtMoney(tx.summa)}
                            </Money>
                          </TableCell>
                          {/* Usul faqat "to'lov" qatorlarida bo'ladi — qarz
                              ochilganda pul harakat qilmagan. */}
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {usulLabel(tx.tolov_usuli)}
                          </TableCell>
                          <TableCell className="max-w-[14rem] truncate text-muted-foreground">
                            {tx.izoh || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </div>
      </Dialog>

      {pay && data && (
        <TolovDialog
          supplierId={supplierId}
          balans={data.balans}
          onClose={() => setPay(false)}
          onSaved={() => {
            setPay(false);
            bump();
          }}
        />
      )}
      {manual && data && (
        <QoldaDialog
          supplierId={supplierId}
          onClose={() => setManual(false)}
          onSaved={() => {
            setManual(false);
            bump();
          }}
        />
      )}
      <ConfirmDialog
        open={confirmOff}
        onOpenChange={setConfirmOff}
        title={t("yetkazib.confirmOff.title")}
        description={t("yetkazib.confirmOff.desc")}
        confirmLabel={t("yetkazib.detail.deactivate")}
        destructive
        onConfirm={deactivate}
      />
    </>
  );
}

/* ─────────────────────────── To'lov / qo'lda qarz ─────────────────────────── */

function TolovDialog({
  supplierId,
  balans,
  onClose,
  onSaved,
}: {
  supplierId: number;
  balans: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const qoldiq = Math.max(0, Number(balans));
  // Qoldiq bilan oldindan to'ldiramiz — eng ko'p uchraydigan holat.
  const [summa, setSumma] = useState(qoldiq ? String(Math.round(qoldiq)) : "");
  const [izoh, setIzoh] = useState("");
  // Pul QANDAY berilgani. Ixtiyoriy — tanlanmasa daftar qatori "noma'lum".
  const [usul, setUsul] = useState<PaymentMethod | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const n = Number(summa) || 0;
  // Ortiqcha to'lov mumkin, lekin JIMGINA emas: server tasdiqsiz rad etadi
  // (mijoz tomonidagi `POST /debts/tolov` bilan AYNI qoida). Shuning uchun bu
  // yerda ham ikki bosqichli tasdiq bor.
  const excess = n - qoldiq;
  const [avans, setAvans] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (n <= 0) {
      setErr(t("yetkazib.tolovDialog.amountRequired"));
      return;
    }
    // Birinchi bosqich: ortiqcha summa kiritilgan, hali tasdiqlanmagan.
    if (excess > 0 && !avans) {
      setAvans(true);
      setErr(null);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.supplierTolov({
        supplier_id: supplierId,
        summa: String(n),
        tolov_usuli: usul || null,
        izoh: izoh.trim() || null,
        avans_tasdiqlandi: avans,
      });
      toast.success(t("yetkazib.tolovDialog.saved"));
      onSaved();
    } catch (e) {
      const ae = e as ApiError;
      // Balans forma ochilgandan keyin o'zgargan bo'lishi mumkin.
      if (ae.code === "payment_exceeds_balance") {
        setAvans(true);
        setErr(
          t("yetkazib.tolovDialog.overpayWarn", {
            summa: fmtMoney(String(ae.details?.ortiqcha ?? excess)),
          }),
        );
      } else {
        setErr(ae.message || t("common:common.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} contentClassName="max-w-sm">
      <form onSubmit={submit} className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("yetkazib.tolovDialog.title")}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("yetkazib.tolovDialog.qoldiq")}: {fmtMoney(qoldiq)}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sup-tolov">{t("yetkazib.tolovDialog.summa")}</Label>
          <MoneyInput
            id="sup-tolov"
            value={summa}
            onChange={(v) => {
              setSumma(v);
              // Yangi raqamni ko'rmasdan avans tasdiqlanib qolmasin.
              setAvans(false);
              setErr(null);
            }}
            disabled={busy}
            className="text-right tabular-nums"
            placeholder="0"
          />
        </div>

        <PaymentMethodSelect
          id="sup-usul"
          value={usul}
          onChange={setUsul}
          disabled={busy}
          className="space-y-1.5"
        />

        {excess > 0 && (
          <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            {t("yetkazib.tolovDialog.overpayWarn", {
              summa: fmtMoney(excess),
            })}
          </div>
        )}

        <Input
          value={izoh}
          onChange={(e) => setIzoh(e.target.value)}
          disabled={busy}
          placeholder={t("yetkazib.tolovDialog.izohPlaceholder")}
        />

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="flex-none"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" disabled={busy || n <= 0} className="flex-1">
            {busy
              ? t("common:actions.processing")
              : excess > 0
                ? t("yetkazib.tolovDialog.submitAnyway")
                : t("yetkazib.tolovDialog.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function QoldaDialog({
  supplierId,
  onClose,
  onSaved,
}: {
  supplierId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [summa, setSumma] = useState("");
  const [izoh, setIzoh] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const n = Number(summa) || 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Izoh bu yerda MAJBURIY (backend `min_length=1`) — hujjatsiz qarzning
    // sababi yozilmasa daftar keyinroq o'qib bo'lmas holga keladi.
    if (n <= 0 || !izoh.trim()) {
      setErr(t("yetkazib.qoldaDialog.required"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.supplierQolda({
        supplier_id: supplierId,
        summa: String(n),
        izoh: izoh.trim(),
      });
      toast.success(t("yetkazib.qoldaDialog.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} contentClassName="max-w-sm">
      <form onSubmit={submit} className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("yetkazib.qoldaDialog.title")}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("yetkazib.qoldaDialog.desc")}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sup-qolda">{t("yetkazib.qoldaDialog.summa")}</Label>
          <MoneyInput
            id="sup-qolda"
            value={summa}
            onChange={setSumma}
            disabled={busy}
            className="text-right tabular-nums"
            placeholder="0"
          />
        </div>
        <Input
          value={izoh}
          onChange={(e) => setIzoh(e.target.value)}
          disabled={busy}
          placeholder={t("yetkazib.qoldaDialog.izohPlaceholder")}
        />
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="flex-none"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={busy || n <= 0 || !izoh.trim()}
            className="flex-1"
          >
            {busy ? t("common:actions.processing") : t("yetkazib.qoldaDialog.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/* ───────────────────────── Kartochka (yaratish/tahrir) ───────────────────────── */

function SupplierFormDialog({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: SupplierDraft | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [name, setName] = useState(supplier?.name ?? "");
  const [phone, setPhone] = useState(initialPhoneInput(supplier?.phone));
  const [contact, setContact] = useState(supplier?.contact_person ?? "");
  const [address, setAddress] = useState(supplier?.address ?? "");
  const [note, setNote] = useState(supplier?.note ?? "");
  const [faol, setFaol] = useState(supplier?.faol ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setErr(t("yetkazib.form.nameRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    const body = {
      name: name.trim(),
      phone: isBlankPhoneInput(phone) ? null : phone.trim(),
      contact_person: contact.trim() || null,
      address: address.trim() || null,
      note: note.trim() || null,
    };
    try {
      if (supplier) {
        await api.updateSupplier(supplier.id, { ...body, faol });
      } else {
        await api.createSupplier(body);
      }
      toast.success(t("yetkazib.form.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} contentClassName="max-w-md">
      <form onSubmit={submit} className="space-y-3 p-5">
        <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
          {supplier ? t("yetkazib.form.editTitle") : t("yetkazib.form.newTitle")}
        </h2>

        <div className="space-y-1.5">
          <Label htmlFor="sup-name">{t("yetkazib.form.nomi")}</Label>
          <Input
            id="sup-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="sup-phone">{t("yetkazib.form.telefon")}</Label>
            <Input
              id="sup-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
              placeholder="+998 90 123 45 67"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sup-contact">{t("yetkazib.form.aloqa")}</Label>
            <Input
              id="sup-contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sup-address">{t("yetkazib.form.manzil")}</Label>
          <Input
            id="sup-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sup-note">{t("yetkazib.form.izoh")}</Label>
          <Textarea
            id="sup-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            rows={2}
          />
        </div>
        {supplier && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={faol}
              onChange={(e) => setFaol(e.target.checked)}
              disabled={busy}
              className="size-4 accent-primary"
            />
            {t("yetkazib.form.faol")}
          </label>
        )}

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="flex-none"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" disabled={busy} className="flex-1">
            {busy ? t("common:actions.processing") : t("yetkazib.form.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
